import React, { useState, useEffect, useCallback } from 'react';
import { captureAPI } from '../services/api';
import './DepartmentsSettingsPage.css';

const PRESET_COLORS = [
  '#2C6FB1','#10b981','#f59e0b','#8b5cf6',
  '#ec4899','#6b7280','#14b8a6','#9ca3af',
  '#ef4444','#f97316','#84cc16','#06b6d4',
];

export default function DepartmentsSettingsPage() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDept, setNewDept] = useState({ label: '', description: '', color: '#2C6FB1' });
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await captureAPI.getDepartments();
      setDepartments(res?.departments || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleActive = async (dept) => {
    setSaving(dept.id);
    try {
      await captureAPI.updateDepartment(dept.id, { is_active: !dept.is_active });
      setDepartments(prev => prev.map(d => d.id === dept.id ? { ...d, is_active: !d.is_active } : d));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const handleColorChange = async (dept, color) => {
    setSaving(dept.id);
    try {
      await captureAPI.updateDepartment(dept.id, { color });
      setDepartments(prev => prev.map(d => d.id === dept.id ? { ...d, color } : d));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update color');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (dept) => {
    if (dept.is_system) return;
    if (!window.confirm(`Delete department "${dept.label}"? This cannot be undone.`)) return;
    setSaving(dept.id);
    try {
      await captureAPI.deleteDepartment(dept.id);
      setDepartments(prev => prev.filter(d => d.id !== dept.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
    } finally {
      setSaving(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newDept.label) { setAddError('Label is required'); return; }
    setAdding(true);
    setAddError(null);
    try {
      const res = await captureAPI.createDepartment(newDept);
      setDepartments(prev => [...prev, res.department]);
      setNewDept({ label: '', description: '', color: '#2C6FB1' });
      setShowAddForm(false);
    } catch (err) {
      setAddError(err?.response?.data?.error || 'Failed to create department');
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <div className="dps-page"><p className="dps-loading">Loading departments…</p></div>;

  return (
    <div className="dps-page">
      <div className="dps-header">
        <div>
          <h2>Departments</h2>
          <p className="dps-subtitle">
            Departments determine ownership and cross-visibility of fields and documents. Used for filtering in the Document Library.
          </p>
        </div>
        <button className="dps-add-btn" onClick={() => setShowAddForm(v => !v)}>
          {showAddForm ? 'Cancel' : '+ Add Department'}
        </button>
      </div>

      {error && (
        <div className="dps-error">
          {error}
          <button className="dps-error-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {showAddForm && (
        <form className="dps-add-form" onSubmit={handleAdd}>
          <div className="dps-add-row">
            <div className="dps-add-field" style={{ flex: 2 }}>
              <label>Label</label>
              <input
                type="text"
                placeholder="e.g. Legal"
                value={newDept.label}
                onChange={e => setNewDept(p => ({ ...p, label: e.target.value }))}
                required
                autoFocus
              />
              <span className="dps-add-hint">
                Key: <code>{newDept.label ? newDept.label.toLowerCase().replace(/[^a-z0-9\s]/g,'').trim().replace(/\s+/g,'_') || '…' : '…'}</code>
              </span>
            </div>
            <div className="dps-add-field" style={{ flex: 3 }}>
              <label>Description</label>
              <input
                type="text"
                placeholder="e.g. Manages legal documents and agreements"
                value={newDept.description}
                onChange={e => setNewDept(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="dps-add-field">
              <label>Color</label>
              <div className="dps-color-picker">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`dps-color-swatch ${newDept.color === c ? 'active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setNewDept(p => ({ ...p, color: c }))}
                  />
                ))}
              </div>
            </div>
            <button type="submit" className="dps-create-btn" disabled={adding}>
              {adding ? 'Creating…' : 'Create'}
            </button>
          </div>
          {addError && <p className="dps-add-error">{addError}</p>}
        </form>
      )}

      <div className="dps-list">
        {departments.map(dept => (
          <div
            key={dept.id}
            className={['dps-card', !dept.is_active ? 'dps-inactive' : '', saving === dept.id ? 'dps-saving' : ''].filter(Boolean).join(' ')}
            style={{ borderLeft: `4px solid ${dept.color || '#9ca3af'}` }}
          >
            <div className="dps-card-left">
              <div
                className="dps-color-dot"
                style={{ background: dept.color || '#9ca3af' }}
              />
              <div className="dps-card-info">
                <div className="dps-card-label">
                  {dept.label}
                  {dept.is_system && <span className="dps-system-badge">system</span>}
                </div>
                {dept.description && <div className="dps-card-desc">{dept.description}</div>}
                <code className="dps-card-key">{dept.key}</code>
              </div>
            </div>

            <div className="dps-card-right">
              {/* Color swatches */}
              <div className="dps-inline-colors">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`dps-color-swatch sm ${dept.color === c ? 'active' : ''}`}
                    style={{ background: c }}
                    onClick={() => handleColorChange(dept, c)}
                    title={c}
                    disabled={saving === dept.id}
                  />
                ))}
              </div>

              <div className="dps-card-actions">
                <label className="dps-active-toggle" title={dept.is_active ? 'Deactivate' : 'Activate'}>
                  <input
                    type="checkbox"
                    checked={dept.is_active}
                    onChange={() => handleToggleActive(dept)}
                    disabled={saving === dept.id}
                  />
                  <span>{dept.is_active ? 'Active' : 'Inactive'}</span>
                </label>
                {!dept.is_system && (
                  <button
                    className="dps-delete-btn"
                    onClick={() => handleDelete(dept)}
                    disabled={saving === dept.id}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dps-footer">
        <span>{departments.filter(d => d.is_system).length} system departments</span>
        <span>·</span>
        <span>{departments.filter(d => !d.is_system).length} custom departments</span>
        <span>·</span>
        <span>{departments.filter(d => d.is_active).length} active</span>
      </div>
    </div>
  );
}
